if (!customElements.get('product-gift-card')) {
    const MAX_MESSAGE_CHARS = 200;
    const MAX_SEND_DAYS = 90;

    const PROPERTY_KEYS = {
        sendToRecipient: '__shopify_send_gift_card_to_recipient',
        email: 'Recipient email',
        name: 'Recipient name',
        message: 'Message',
        sendOn: 'Send on',
        offset: '__shopify_offset',
    };

    const FIELD_MAP = {
        email: 'email',
        name: 'first-name',
        'first-name': 'first-name',
        'last-name': 'last-name',
        message: 'message',
        send_on: 'send-on',
        'send-on': 'send-on',
    };

    class ProductGiftCard extends HTMLElement {
        #timezoneOffset = '';

        constructor() {
            super();

            this.elToggle = this.querySelector('[data-gift-card-toggle]');
            this.elFields = this.querySelector('[data-recipient-fields]');
            this.elEmail = this.querySelector('[data-recipient-email]');
            this.elFirstName = this.querySelector('[data-recipient-first-name]');
            this.elLastName = this.querySelector('[data-recipient-last-name]');
            this.elMessage = this.querySelector('[data-recipient-message]');
            this.elSendOnDisplay = this.querySelector('[data-recipient-send-on-display]');
            this.elSendOnValue = this.querySelector('[data-recipient-send-on]');
            this.elSendOnNative = this.querySelector('[data-recipient-send-on-native]');
            this.elSendOnTrigger = this.querySelector('[data-recipient-send-on-trigger]');
            this.elMessageCount = this.querySelector('[data-message-count]');
            this.elErrorSummary = this.querySelector('[data-error-summary]');
            this.elErrorList = this.querySelector('[data-error-list]');
            this.elLiveRegion = this.querySelector(`#gift-card-fields-live-region-${this.dataset.sectionId}`);
        }

        connectedCallback() {
            this.#timezoneOffset = new Date().getTimezoneOffset().toString();
            this.#setSendOnLimits();

            this.elToggle?.addEventListener('change', this.#onToggleChange.bind(this));
            this.elMessage?.addEventListener('input', this.#updateMessageCount.bind(this));
            this.elSendOnDisplay?.addEventListener('blur', this.#syncSendOnFromDisplay.bind(this));
            this.elSendOnDisplay?.addEventListener('keydown', this.#handleSendOnDisplayKeydown.bind(this));
            this.elSendOnTrigger?.addEventListener('click', this.#openSendOnPicker.bind(this));
            this.elSendOnNative?.addEventListener('click', this.#openSendOnPicker.bind(this));
            this.elSendOnNative?.addEventListener('change', this.#syncSendOnFromNative.bind(this));

            for (const elInput of this.#inputFields()) {
                elInput?.addEventListener('input', () => this.#clearFieldError(elInput));
            }

            this.#onToggleChange();
        }

        get isEnabled() {
            return Boolean(this.elToggle?.checked);
        }

        updateVariantId(variantId) {
            this.dataset.productVariantId = String(variantId);
        }

        validate() {
            this.clearErrors();

            if (!this.isEnabled) {
                return { valid: true, errors: {} };
            }

            this.#syncSendOnFromDisplay();

            const errors = {};

            const email = this.elEmail?.value.trim() ?? '';
            if (!email) {
                errors.email = window.theme?.strings?.giftCard?.recipient?.email_required
                    ?? 'Please enter your recipient\'s email address';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                errors.email = window.theme?.strings?.giftCard?.recipient?.email_invalid
                    ?? 'Please enter a valid email address';
            }

            const firstName = this.elFirstName?.value.trim() ?? '';
            if (!firstName) {
                errors['first-name'] = window.theme?.strings?.giftCard?.recipient?.first_name_required
                    ?? 'Please enter your recipient\'s first name';
            }

            const message = this.elMessage?.value ?? '';
            if (message.length > MAX_MESSAGE_CHARS) {
                errors.message = window.theme?.strings?.giftCard?.recipient?.message_max
                    ?? 'Message must be 200 characters or fewer';
            }

            const sendOn = this.#getSendOnIso();
            if (sendOn && !this.#isValidSendOnDate(sendOn)) {
                errors['send-on'] = window.theme?.strings?.giftCard?.recipient?.send_on_invalid
                    ?? 'Please select a date within 90 days';
            } else if (this.elSendOnDisplay?.value.trim() && !sendOn) {
                errors['send-on'] = window.theme?.strings?.giftCard?.recipient?.send_on_format_invalid
                    ?? 'Please enter a valid date (MM/DD/YYYY)';
            }

            if (Object.keys(errors).length) {
                this.#showErrors(errors);
                return { valid: false, errors };
            }

            return { valid: true, errors: {} };
        }

        getPropertiesForCart() {
            if (!this.isEnabled) {
                return {};
            }

            this.#syncSendOnFromDisplay();

            const properties = {
                [PROPERTY_KEYS.sendToRecipient]: 'true',
                [PROPERTY_KEYS.email]: this.elEmail?.value.trim() ?? '',
                [PROPERTY_KEYS.offset]: this.#timezoneOffset,
            };

            const recipientName = [this.elFirstName?.value.trim(), this.elLastName?.value.trim()]
                .filter(Boolean)
                .join(' ');

            if (recipientName) {
                properties[PROPERTY_KEYS.name] = recipientName;
            }

            const message = this.elMessage?.value.trim() ?? '';
            if (message) {
                properties[PROPERTY_KEYS.message] = message;
            }

            const sendOn = this.#getSendOnIso();
            if (sendOn) {
                properties[PROPERTY_KEYS.sendOn] = sendOn;
            }

            return properties;
        }

        displayApiErrors(response) {
            this.clearErrors();

            const errors = this.#parseApiErrors(response);
            if (!Object.keys(errors).length && response?.description) {
                errors.form = response.description;
            }

            this.#showErrors(errors);
        }

        reset() {
            if (!this.elToggle) return;

            this.elToggle.checked = false;
            this.#clearInputFields();
            this.clearErrors();
            this.#onToggleChange();
        }

        clearErrors() {
            this.elErrorSummary?.setAttribute('hidden', '');
            if (this.elErrorList) {
                this.elErrorList.innerHTML = '';
            }

            for (const elError of this.querySelectorAll('[data-field-error]')) {
                elError.textContent = '';
                elError.setAttribute('hidden', '');
            }

            for (const elWrapper of this.querySelectorAll('[data-field]')) {
                elWrapper.classList.remove('product-gift-card--input--error');
            }

            for (const elInput of this.#inputFields()) {
                elInput?.removeAttribute('aria-invalid');
                elInput?.removeAttribute('aria-describedby');
            }
        }

        #inputFields() {
            return [this.elEmail, this.elFirstName, this.elLastName, this.elMessage, this.elSendOnDisplay];
        }

        #disableableFields() {
            return [...this.#inputFields(), this.elSendOnNative, this.elSendOnTrigger];
        }

        #onToggleChange() {
            if (this.isEnabled) {
                this.#enableInputFields();
                this.elFields?.removeAttribute('hidden');
                if (this.elLiveRegion) {
                    this.elLiveRegion.textContent = window.theme?.strings?.giftCard?.recipient?.expanded
                        ?? 'Gift recipient form expanded';
                }
            } else {
                this.#clearInputFields();
                this.#disableInputFields();
                this.elFields?.setAttribute('hidden', '');
                this.clearErrors();
                if (this.elLiveRegion) {
                    this.elLiveRegion.textContent = window.theme?.strings?.giftCard?.recipient?.collapsed
                        ?? 'Gift recipient form collapsed';
                }
            }
        }

        #enableInputFields() {
            for (const elInput of this.#disableableFields()) {
                elInput.disabled = false;
            }
        }

        #disableInputFields() {
            for (const elInput of this.#disableableFields()) {
                elInput.disabled = true;
            }
        }

        #clearInputFields() {
            for (const elInput of this.#inputFields()) {
                if (elInput) {
                    elInput.value = '';
                }
            }

            if (this.elSendOnValue) {
                this.elSendOnValue.value = '';
            }

            if (this.elSendOnNative) {
                this.elSendOnNative.value = '';
            }

            this.#updateMessageCount();
        }

        #updateMessageCount() {
            if (!this.elMessageCount || !this.elMessage) return;

            const count = this.elMessage.value.length;
            const charactersLabel = window.theme?.strings?.giftCard?.recipient?.characters ?? 'Characters';
            this.elMessageCount.textContent = `${count}/${MAX_MESSAGE_CHARS} ${charactersLabel}`;
        }

        #setSendOnLimits() {
            if (!this.elSendOnNative) return;

            const today = new Date();
            const maxDate = new Date(today);
            maxDate.setDate(maxDate.getDate() + MAX_SEND_DAYS);

            this.elSendOnNative.min = this.#formatIsoDate(today);
            this.elSendOnNative.max = this.#formatIsoDate(maxDate);
        }

        #formatIsoDate(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }

        #formatIsoToUs(iso) {
            const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
            if (!match) return iso;

            return `${match[2]}/${match[3]}/${match[1]}`;
        }

        #parseUsToIso(value) {
            const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim());
            if (!match) return null;

            const month = Number(match[1]);
            const day = Number(match[2]);
            const year = Number(match[3]);

            if (month < 1 || month > 12 || day < 1 || day > 31) {
                return null;
            }

            const date = new Date(year, month - 1, day);
            if (
                date.getFullYear() !== year
                || date.getMonth() !== month - 1
                || date.getDate() !== day
            ) {
                return null;
            }

            return this.#formatIsoDate(date);
        }

        #getSendOnIso() {
            return this.elSendOnValue?.value ?? '';
        }

        #syncSendOnFromDisplay() {
            const display = this.elSendOnDisplay?.value.trim() ?? '';

            if (!display) {
                if (this.elSendOnValue) this.elSendOnValue.value = '';
                if (this.elSendOnNative) this.elSendOnNative.value = '';
                return;
            }

            const iso = this.#parseUsToIso(display);
            if (!iso) {
                if (this.elSendOnValue) this.elSendOnValue.value = '';
                return;
            }

            if (this.elSendOnValue) this.elSendOnValue.value = iso;
            if (this.elSendOnNative) this.elSendOnNative.value = iso;
            if (this.elSendOnDisplay) this.elSendOnDisplay.value = this.#formatIsoToUs(iso);
        }

        #syncSendOnFromNative() {
            const iso = this.elSendOnNative?.value ?? '';
            if (!iso) return;

            if (this.elSendOnValue) this.elSendOnValue.value = iso;
            if (this.elSendOnDisplay) this.elSendOnDisplay.value = this.#formatIsoToUs(iso);
            this.#clearFieldError(this.elSendOnDisplay);

            if (!window.matchMedia('(pointer: coarse)').matches) {
                this.elSendOnDisplay?.focus();
            }
        }

        #openSendOnPicker() {
            if (!this.elSendOnNative || this.elSendOnNative.disabled) return;

            try {
                if (typeof this.elSendOnNative.showPicker === 'function') {
                    this.elSendOnNative.showPicker();
                    return;
                }
            } catch {
            }

            this.elSendOnNative.focus();
        }

        #handleSendOnDisplayKeydown(ev) {
            if (ev.key !== 'ArrowDown') return;

            ev.preventDefault();
            this.#openSendOnPicker();
        }

        #isValidSendOnDate(value) {
            if (!this.elSendOnNative?.min || !this.elSendOnNative?.max) {
                return Boolean(value);
            }

            return value >= this.elSendOnNative.min && value <= this.elSendOnNative.max;
        }

        #parseApiErrors(response) {
            const errors = {};

            if (response?.errors && typeof response.errors === 'object') {
                for (const [key, value] of Object.entries(response.errors)) {
                    const message = Array.isArray(value) ? value.join(', ') : String(value);
                    const fieldKey = FIELD_MAP[key] ?? key;
                    errors[fieldKey] = message;
                }
            }

            if (response?.description && !Object.keys(errors).length) {
                const description = String(response.description);

                if (/email/i.test(description)) {
                    errors.email = description;
                } else if (/name/i.test(description)) {
                    errors['first-name'] = description;
                } else if (/message/i.test(description)) {
                    errors.message = description;
                } else if (/date|send/i.test(description)) {
                    errors['send-on'] = description;
                } else {
                    errors.form = description;
                }
            }

            return errors;
        }

        #showErrors(errors) {
            const listItems = [];

            for (const [fieldKey, message] of Object.entries(errors)) {
                if (fieldKey === 'form') {
                    listItems.push(message);
                    continue;
                }

                const elError = this.querySelector(`[data-field-error="${fieldKey}"]`);
                const elWrapper = this.querySelector(`[data-field="${fieldKey}"]`);
                const elInput = this.#getInputForField(fieldKey);

                if (elError) {
                    elError.textContent = message;
                    elError.removeAttribute('hidden');
                }

                elWrapper?.classList.add('product-gift-card--input--error');

                if (elInput) {
                    elInput.setAttribute('aria-invalid', 'true');
                    if (elError?.id) {
                        elInput.setAttribute('aria-describedby', elError.id);
                    }
                }

                listItems.push(message);
            }

            if (listItems.length && this.elErrorSummary && this.elErrorList) {
                this.elErrorSummary.removeAttribute('hidden');
                this.elErrorList.innerHTML = listItems
                    .map((message) => `<li class="product-gift-card--error-item small">${message}</li>`)
                    .join('');
            }
        }

        #clearFieldError(elInput) {
            const elWrapper = elInput.closest('[data-field]');
            const fieldKey = elWrapper?.dataset.field;
            if (!fieldKey) return;

            const elError = this.querySelector(`[data-field-error="${fieldKey}"]`);
            elError?.setAttribute('hidden', '');
            elError && (elError.textContent = '');
            elWrapper?.classList.remove('product-gift-card--input--error');
            elInput.removeAttribute('aria-invalid');
            elInput.removeAttribute('aria-describedby');
        }

        #getInputForField(fieldKey) {
            switch (fieldKey) {
                case 'email':
                    return this.elEmail;
                case 'first-name':
                    return this.elFirstName;
                case 'last-name':
                    return this.elLastName;
                case 'message':
                    return this.elMessage;
                case 'send-on':
                    return this.elSendOnDisplay;
                default:
                    return null;
            }
        }
    }

    customElements.define('product-gift-card', ProductGiftCard);
}
